import { getWithAuth, postWithAuth } from '@/api/httpRequests'
import Config from '@/shared/config/config'
import {
    getFromLocalStorage,
    removeFromLocalStorage,
    sessionTokenLsKeyword,
} from '@/shared/localStorage/localStorageRepository'

import { ITVServiceStreamIQ } from '@/shared/types/services/types/tvService/ITVServiceStreamIQ'
import { ChannelDataType, ChannelType } from '@/shared/types/appTypes'
import {
    StreamIQChannel,
    StreamIQChannelData,
} from '@/clients/streamiq/shared/classes/StreamIqChannel'
import { mapStreamIQToChannelProps } from '@/clients/streamiq/shared/lib/mapStreamIQToChannelProps'
import { LAST_ACTUAL_PROFILE, tvDataKey } from '@/shared/constants'
import { deviceId } from '@/api/streemiq/apiConstantsStreemIQ'
import { getConfigRepository } from '@/shared/getContainer/getConfigRepository'
import {
    getErrorMessage,
    getEtagFromLocalStorage,
    setEtagToLocalStorage,
} from '@/shared/lib'
import { getBootstraps } from '@/clients/streamiq/shared/lib'

const ConfigRepository = await getConfigRepository()
const configRepository = ConfigRepository.Instance

class TVServiceStreamIQ implements ITVServiceStreamIQ {
    getFilteredChannelList = (channelsList: ChannelDataType[]) => {
        const filteredChannels = channelsList.filter(
            (channel: ChannelDataType) => !!channel
        )
        return filteredChannels
    }

    getChannelObjectList = (channels: ChannelDataType[]): StreamIQChannel[] => {
        const channelObjectsList = channels.map(
            (channel: ChannelDataType): StreamIQChannel =>
                new StreamIQChannel(channel as StreamIQChannelData)
        )
        return channelObjectsList
    }
    getChannelURL = async (selectedChannel: ChannelType) => {
        const bootstraps = getBootstraps()

        const url = bootstraps?.services?.['session-service'].session
        const actualProfile = getFromLocalStorage(LAST_ACTUAL_PROFILE)
        try {
            const response = await postWithAuth(
                url,
                false,
                JSON.stringify({
                    streamingProtocol: 'hls',
                    profileId: actualProfile,
                    deviceFingerprint: deviceId,
                    reference: {
                        id: selectedChannel?.id || '',
                        type: 'channel',
                        serviceName: 'epg-service',
                    },
                })
            )
            if (response) return response.playInfo.url
        } catch (error) {
            console.log(error)
        }
    }
    getStreamIQChannelUri = async (selectedChannel: ChannelType) => {
        try {
            return await this.getChannelURL(selectedChannel)
        } catch (error) {
            console.log('error', error)
        }
    }

    getApiChannelsList = async () => {
        const token: string | null = getFromLocalStorage(sessionTokenLsKeyword)
        if (!token) return undefined
        const bootstraps = getBootstraps()

        const etag = getEtagFromLocalStorage(tvDataKey)

        try {
            let res
            if (Date.now() > Number(etag.requestTime) || !etag) {
                res = await getWithAuth(
                    bootstraps.services['epg-service'].channel,
                    false
                )

                if (etag.eTag === res?.eTag) return
                removeFromLocalStorage(tvDataKey)
            }

            if (!res) return

            const { response, maxAge, eTag, requestTime } = res || {}

            if (maxAge && eTag && requestTime) {
                setEtagToLocalStorage(tvDataKey, maxAge, requestTime, eTag)
            }
            return response.resources.map(
                (channel: StreamIQChannelData, index: number) => {
                    channel.lcn = (index + 1).toString()
                    return mapStreamIQToChannelProps(channel)
                }
            )
        } catch (error) {
            getErrorMessage('getApiChannelsList', error)
            removeFromLocalStorage(sessionTokenLsKeyword)
        }

        return
    }

    getTvChannelsList = (channels: ChannelType[]) => {
        if (!channels) return []
        const tvChannels = channels.filter(
            (channel: ChannelType) => channel.service === 0
        )

        return tvChannels
    }

    getRadioChannelsList = (channels: ChannelType[]) => {
        if (!channels) return []
        const tvChannels = channels.filter(
            (channel: ChannelType) => channel.service === 1
        )
        return tvChannels
    }

    getGroupedTvRadioChannels = (channels: ChannelType[]) => {
        const tvChannels = this.getTvChannelsList(channels)

        return tvChannels
    }

    checkIfChannelsVersionChanged = async (
        previousLocalConfig: Config | null | undefined
    ) => {
        if (!previousLocalConfig) return false
        const newConfig = configRepository.getLocalConfig()
        if (newConfig) {
            return (
                previousLocalConfig.channelsVersion !==
                newConfig.channelsVersion
            )
        }
    }
}

export default new TVServiceStreamIQ()
